import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Toolbar
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { strings } from "../../../localization/LocalizedStrings";
import { AlertDialog } from "../../AlertDialog";
import { SwitchListItem } from "../../SwitchListItem";
import { FieldTitle } from "../../Text/FieldTitle";
import { ContainedButton } from "../../ContainedButton";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { ShelfSegmentAlbumShape } from "../../../hooks/shelfSegments";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";

export const ModifyShelfSegmentAlbumDialog: FunctionComponent<
  IModifyShelfSegmentAlbumDialogProps
> = ({ open, setOpen, onSuccess }) => {
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    setIsConfirmCloseAlertOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const formik = useFormik({
    initialValues: {
      shape: "longPlay" as ShelfSegmentAlbumShape,
      removeFromPreviousLocations: false
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ shape, removeFromPreviousLocations }) => {
      handleClose();
      onSuccess(shape, removeFromPreviousLocations);
    }
  });
  useEffect(() => {
    if (!open) return;
    formik.resetForm();
  }, [open]);
  const handleShapeChange = (
    event: React.MouseEvent<HTMLElement>,
    shape: ShelfSegmentAlbumShape
  ) => {
    if (shape !== null) {
      formik.setFieldValue("shape", shape);
    }
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"sm"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{strings.shelf_segment_add_album_title}</h3>
          <Box sx={{ flexGrow: 1 }} />
          <ToolbarButton onClick={tryToClose} tooltip={strings.close} iconType={IconType.Close} />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.shelf_segment_album_shape_title}</FieldTitle>
          <SwitchListItem
            className={styles.visibility}
            labels={[
              strings.shelf_segment_album_shape_long_play,
              strings.shelf_segment_album_shape_ten_inches,
              strings.shelf_segment_album_shape_single,
              strings.shelf_segment_album_shape_cd,
              strings.shelf_segment_album_shape_box_set
            ]}
            values={["longPlay", "tenInches", "single", "compactDisc", "boxSet"]}
            value={formik.values.shape}
            fullWidth={true}
            disableGutters
            handleChange={handleShapeChange}
          />
          <FormControlLabel
            sx={{ width: "auto" }}
            control={
              <Checkbox
                checked={formik.values.removeFromPreviousLocations}
                onChange={() =>
                  formik.setFieldValue(
                    "removeFromPreviousLocations",
                    !formik.values.removeFromPreviousLocations
                  )
                }
              />
            }
            label={strings.layout_remove_from_all_segments}
          />
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={handleClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
    </FormikProvider>
  );
};

export interface IModifyShelfSegmentAlbumDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  onSuccess: (shape: ShelfSegmentAlbumShape, removeFromPreviousLocations: boolean) => void;
}
